import React, { useState } from 'react';
import { Database, Trash2, TestTube, Eye, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { RemoteServer, RemoteServerType } from '../../types/remoteServer';
import { deleteRemoteServer, testRemoteServerConnection } from '../../services/remoteServerService';
import RemoteTablesBrowser from './RemoteTablesBrowser';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

interface RemoteServersListProps {
  servers: RemoteServer[];
  onServerDeleted: () => void;
  onServerUpdated: () => void;
}

const RemoteServersList: React.FC<RemoteServersListProps> = ({
  servers,
  onServerDeleted,
  onServerUpdated,
}) => {
  const [deleteServerId, setDeleteServerId] = useState<string | null>(null);
  const [testingServerId, setTestingServerId] = useState<string | null>(null);
  const [browsingServerId, setBrowsingServerId] = useState<string | null>(null);
  const { toast } = useToast();

  const handleDelete = async () => {
    if (!deleteServerId) return;

    try {
      await deleteRemoteServer(deleteServerId);
      onServerDeleted();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete integration',
        variant: 'destructive',
      });
    } finally {
      setDeleteServerId(null);
    }
  };

  const handleTestConnection = async (serverId: string) => {
    setTestingServerId(serverId);
    try {
      const result = await testRemoteServerConnection(serverId);
      if (result.success) {
        toast({
          title: 'Connection successful',
          description: `Latency: ${result.details?.latencyMs}ms`,
        });
      } else {
        toast({
          title: 'Connection failed',
          description: result.message,
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Connection test failed',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setTestingServerId(null);
    }
  };

  const getServerIcon = (type: RemoteServerType) => {
    if (type === RemoteServerType.STRIPE_FDW) {
      return '💳';
    }
    return '🐘';
  };

  const getServerTypeLabel = (type: RemoteServerType) => {
    if (type === RemoteServerType.STRIPE_FDW) {
      return 'Stripe';
    }
    return 'PostgreSQL';
  };

  return (
    <>
      <div className="grid gap-4">
        {servers.map((server) => (
          <Card key={server.id}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="text-3xl">{getServerIcon(server.foreignDataWrapperType)}</div>
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {server.label}
                      <Badge variant="secondary">
                        {getServerTypeLabel(server.foreignDataWrapperType)}
                      </Badge>
                    </CardTitle>
                    <CardDescription className="mt-1">
                      {server.foreignDataWrapperType === RemoteServerType.POSTGRES_FDW && (
                        <>
                          {(server.foreignDataWrapperOptions as any).host}:
                          {(server.foreignDataWrapperOptions as any).port} /
                          {(server.foreignDataWrapperOptions as any).dbname}
                        </>
                      )}
                      {server.foreignDataWrapperType === RemoteServerType.STRIPE_FDW && (
                        <>Connected to Stripe</>
                      )}
                    </CardDescription>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleTestConnection(server.id)}
                  disabled={testingServerId === server.id}
                >
                  {testingServerId === server.id ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Testing...
                    </>
                  ) : (
                    <>
                      <TestTube className="mr-2 h-4 w-4" />
                      Test Connection
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setBrowsingServerId(server.id)}
                >
                  <Eye className="mr-2 h-4 w-4" />
                  Browse Tables
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDeleteServerId(server.id)}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteServerId} onOpenChange={() => setDeleteServerId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Integration</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this integration? This will also remove all synced
              tables. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Browse Tables Dialog */}
      {browsingServerId && (
        <RemoteTablesBrowser
          remoteServerId={browsingServerId}
          open={!!browsingServerId}
          onOpenChange={(open) => !open && setBrowsingServerId(null)}
        />
      )}
    </>
  );
};

export default RemoteServersList;
